import { gql } from "@/services/httpAdapter";

const ChangeRequestResponseFragment = gql`
	fragment ChangeRequestResponse on ChangeRequestResponse {
		locationId
		commissionCycleId
		payPeriodId
		position
		userId
		requests {
			_id
			commissionCycleId
			issueSummary
			requestTimestamp
			resolutionTimestamp
			createdOn
			status
			origin
			requestPayPeriodId
			payPeriod {
				_id
				title
			}
			resolvingUser {
				_id
				fullName
			}
			user {
				_id
				fullName
			}
			highlights {
				_id
				color
				columnReference
				rowReference
				tableReference
			}
			chat {
				_id
				createdBy {
					_id
					fullName
					presignedProfilePicture
				}
				message
				createdOn
				attachments {
					_id
					name
					type
					size
					displayName
				}
			}
			supportAssigned
			createdBy {
				_id
				fullName
			}
			position
		}
	}
`;

const AddedCompensationFragment = gql`
	fragment AddedCompensation on WashoutSummaryRow {
		_id
		item
		gross
		tier
		rate
		commission
		originalCommission
		modifiedBy
		columnsAddons {
			field
			input
			tag
		}
		rowIndex
		order
		isAfterSubTotal
	}
`;

const CompensationFragment = gql`
	fragment Compensation on WashoutSummary {
		summary {
			_id
			item
			gross
			tier
			rate
			commission
			originalCommission
			modifiedBy
			columnsAddons {
				field
				input
				tag
			}
		}
		total
	}
`;

const CallActivityPerformanceFragment = gql`
	fragment WashoutCallActivityPerformance on WashoutCallActivityPerformance {
		inboundCalls {
			queue
			individual
			total
		}
		inboundMishandledCalls {
			queue
			individual
			total
		}
		outboundCalls {
			queue
			individual
			total
		}
		outboundMishandledCalls {
			queue
			individual
			total
		}
		successfullyReturnedMishandledCalls {
			queue
			individual
			total
		}
		unattendedMishandledCallsPercent {
			queue
			individual
			total
		}
		averageTurnAroundTime {
			queue
			individual
			total
		}
	}
`;

const ChargebacksFragment = gql`
	fragment Chargebacks on WashoutChargebacks {
		chargebacks {
			_id
			saleDate
			chargebackDate
			type
			saleType
			chargebackInvoiceAmount
			dealNumber
			stockNumber
			saleGross
			chargebackAmount
			chargebackCommission
			unit
		}
		chargebackCommission
		total
	}
`;

const EmployeeFragment = gql`
	fragment Employee on WashoutsEmployee {
		_id
		employeeId
		name
		position
		department
		reportsTo
		communications {
			acceptsTxt
			communicationType
			usage
			value
		}
		payDate
		payRange {
			endDate
			startDate
		}
		store
		daysOut
		ranking
	}
`;

const LongevityFragment = gql`
	fragment Longevity on WashoutLongevity {
		hiredDate
		years
		compensation
		isPresent
	}
`;

const ReviewFragment = gql`
	fragment Review on WashoutReview {
		status
		statusSetBy {
			_id
			fullName
		}
		statusSetOn
	}
`;

const TechnicianSpiffsFragment = gql`
	fragment Spiffs on TechnicianSpiffsBonus {
		data {
			_id
			date
			title
			type
			repairOrderNumbers
			reward
		}
	}
`;

const TreeInformationFragment = gql`
	fragment TreeInformation on TreeInformation {
		title
		variant
	}
`;

const TooltipFragment = gql`
	fragment Tooltip on Tooltip {
		title
	}
`;

const WashoutTreeFragment = gql`
	fragment WashoutTree on NodeInWashoutsSidePanelTree {
		_id
		name
		userId
		commissionCycleId
		payPeriodId
		intervalCode
		startDate
		endDate
		status
		fullName
		position
		isDepartmental
		bold
		disabled
		tooltip {
			...Tooltip
		}
		information {
			...TreeInformation
		}
	}
`;

const SidePanelTreeFragment = gql`
	fragment SidePanelTree on WashoutsSidePanelTree {
		locationId
		position
		tree {
			...WashoutTree
			list {
				...WashoutTree
				list {
					...WashoutTree
					list {
						...WashoutTree
						list {
							...WashoutTree
						}
					}
				}
			}
		}
	}
	${TreeInformationFragment}
	${TooltipFragment}
	${WashoutTreeFragment}
`;

const SalesPerformanceFragment = gql`
	fragment SalesPerformance on WashoutSalesPerformance {
		sales {
			_id
			saleDate
			dealNumber
			customerName
			customerNumber
			salesManagerId
			salesManagerName
			stockNumber
			description
			saleType
			dealType
			adjustable
			frontGrossProfit
			backGrossProfit
			units
			saleAmount
			vehicleInformation {
				make
				model
				modelYear
				trim
			}
			frontProducts {
				_id
				product
				value
			}
			backProducts {
				_id
				product
				value
			}
			frontCommission
			backCommission
			grossCommission
			funded
			external
			origin {
				icon
				tooltip
			}
		}
		totals {
			totalsTypeTitle
			frontGrossProfit
			backGrossProfit
			frontProducts {
				_id
				product
				total
			}
			backProducts {
				_id
				product
				total
			}
			frontCommission
			backCommission
			grossCommission
			units
		}
		columnVisibility {
			columnName
			visibility
		}
	}
`;

export {
	ChangeRequestResponseFragment,
	AddedCompensationFragment,
	CompensationFragment,
	CallActivityPerformanceFragment,
	ChargebacksFragment,
	EmployeeFragment,
	LongevityFragment,
	ReviewFragment,
	TechnicianSpiffsFragment,
	SidePanelTreeFragment,
	SalesPerformanceFragment,
};
